import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material";
import SignIn from "./components/auth/SignIn";
import SignUp from "./components/auth/SignUp";
import { useEffect } from "react";
import {
  initializeAuth,
  initializeSessionTracking,
} from "./services/firebase/auth";
import Navbar from "./components/navigation/Navbar";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import Profile from "./components/profile/Profile";
import AdminRoute from "./components/auth/AdminRoute";
import UserManagement from "./components/admin/UserManagement";
import HomeManagement from "./components/admin/HomeManagement";
import { CircularProgress, Box } from "@mui/material";
import { useState } from "react";
import HomePage from "./components/home/HomePage";
import HomeMarketplace from "./components/marketplace/HomeMarketplace";
import { initializeHomes } from "./services/firebase/homes";
import { useSelector } from "react-redux";
import ItemManagement from "./components/admin/ItemManagement";
import ItemMarketplace from "./components/marketplace/ItemMarketplace";
import NFCCardManagement from "./components/admin/NFCCardManagement";
import GroceryItemManagement from "./components/admin/GroceryItemManagement";
import EditProfile from "./components/profile/EditProfile";
import Marketplace from "./components/marketplace/Marketplace";
import OpenHouse from "./components/events/OpenHouse";
import NfcAuthHandler from "./components/auth/NfcAuthHandler";
import Pool from "./components/pool/Pool";
import EnterPool from "./components/pool/EnterPool";
import GroceryStore from "./components/GroceryStore";
import PassAnimation from "./components/PassAnimation";
import Footer from "./components/layout/Footer";
import ScrollToTop from "./components/layout/ScrollToTop";

// Update theme to include background color
const theme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#dc004e",
    },
    background: {
      default: "#f2f0e4",
    },
  },
  typography: {
    fontFamily: "Midifont, sans-serif",
  },
});

function App() {
  const [authInitialized, setAuthInitialized] = useState(false);
  const user = useSelector((state) => state.auth.user);

  // Add useEffect to set body background color
  useEffect(() => {
    document.body.style.backgroundColor = "#f2f0e4";
    document.body.style.margin = "0";

    return () => {
      document.body.style.backgroundColor = "";
      document.body.style.margin = "";
    };
  }, []);

  useEffect(() => {
    let unsubscribe;
    let cleanupSession;

    const initApp = async () => {
      try {
        unsubscribe = await initializeAuth();
        cleanupSession = initializeSessionTracking();
        setAuthInitialized(true);
      } catch (error) {
        console.error("Error initializing app:", error);
        setAuthInitialized(true);
      }
    };

    initApp();

    return () => {
      if (unsubscribe) unsubscribe();
      if (cleanupSession) cleanupSession();
    };
  }, []);

  useEffect(() => {
    const initHomes = async () => {
      if (user) {
        try {
          await initializeHomes();
        } catch (error) {
          console.error("Error initializing homes:", error);
        }
      }
    };

    initHomes();
  }, [user]);

  if (!authInitialized) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          backgroundColor: "#f2f0e4",
          minHeight: "100vh",
          width: "100%",
        }}
      >
        <Router>
          <ScrollToTop />
          <PassAnimation />
          <Navbar />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/pool" element={<Pool />} />
            <Route path="/pool/enter" element={<EnterPool />} />
            {/* Protected Routes */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketplace"
              element={
                <ProtectedRoute>
                  <Marketplace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/grocery-store"
              element={
                <ProtectedRoute>
                  <GroceryStore />
                </ProtectedRoute>
              }
            />
            {/* Remove these routes 
            <Route
              path="/marketplace/homes"
              element={
                <ProtectedRoute>
                  <HomeMarketplace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketplace/items"
              element={
                <ProtectedRoute>
                  <ItemMarketplace />
                </ProtectedRoute>
              }
            />
            */}
            {/* Admin Routes */}
            <Route
              path="/admin/users"
              element={
                <AdminRoute>
                  <UserManagement />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/homes"
              element={
                <AdminRoute>
                  <HomeManagement />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/items"
              element={
                <AdminRoute>
                  <ItemManagement />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/nfc"
              element={
                <AdminRoute>
                  <NFCCardManagement />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/grocery-items"
              element={
                <AdminRoute>
                  <GroceryItemManagement />
                </AdminRoute>
              }
            />
            <Route
              path="/edit-profile"
              element={
                <ProtectedRoute>
                  <EditProfile />
                </ProtectedRoute>
              }
            />
            {/* Open House Route */}
            <Route path="/open-house" element={<OpenHouse />} />
            {/* NFC Auth Handler Route */}
            <Route path="/auth" element={<NfcAuthHandler />} />
          </Routes>
          <Footer />
        </Router>
      </Box>
    </ThemeProvider>
  );
}

export default App;
